import React, { useState, useRef, useEffect } from 'react';
import { PenTool, Type, Save, X, CheckCircle, Clock, AlertCircle, Maximize2, RotateCcw } from 'lucide-react';

const SignaturePadApp = () => {
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureType, setSignatureType] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [signerLocation, setSignerLocation] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const canvasRef = useRef(null);
  const fullscreenCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const tokenMatch = path.match(/\/signature\/([a-f0-9]+)/);
    
    if (tokenMatch) {
      const token = tokenMatch[1];
      setSessionToken(token);
      loadSessionData(token);
    } else {
      setError('Invalid signature URL');
      setLoading(false);
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSignerLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (geoError) => {
          console.warn('Geolocation not available or denied:', geoError.message);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  // Setup canvas context whenever signatureType changes
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [signatureType]);

  // Setup fullscreen canvas when entering fullscreen
  useEffect(() => {
    if (isFullscreen && fullscreenCanvasRef.current) {
      const canvas = fullscreenCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // If there's existing drawing on the small canvas, copy it over
      if (canvasRef.current && hasDrawn) {
        ctx.drawImage(canvasRef.current, 0, 0);
      }
    }
  }, [isFullscreen]); // eslint-disable-line

  // Prevent body scroll when fullscreen is open
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [isFullscreen]);

  // Add non-passive touch event listeners to prevent scrolling on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const preventScroll = (e) => { e.preventDefault(); };
    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [signatureType]);

  useEffect(() => {
    const canvas = fullscreenCanvasRef.current;
    if (!canvas) return;

    const preventScroll = (e) => { e.preventDefault(); };
    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [isFullscreen]);

  const loadSessionData = async (token) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:3000/api/v1'}/signature-pad/session/${token}`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to load session');
      }

      if (result.success) {
        setSessionData(result.data);
        if (result.data.status === 'signed') {
          setCompleted(true);
        }
      } else {
        throw new Error(result.error || 'Failed to load session');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (signatureType !== 'draw') return;
    setIsDrawing(true);
    setHasDrawn(true);

    const canvas = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current;
    const { x, y } = getCanvasPos(e, canvas);
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || signatureType !== 'draw') return;

    const canvas = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current;
    const { x, y } = getCanvasPos(e, canvas);
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = isFullscreen ? fullscreenCanvasRef.current : canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawn(false);
    setSignaturePreview(null);
  };

  const openFullscreen = () => {
    // Try to lock screen orientation to landscape for a better signing experience.
    // This ensures the user draws on a landscape canvas regardless of how they hold
    // their device.  Falls back gracefully when the API is unavailable or denied.
    if (window.screen.orientation && typeof window.screen.orientation.lock === 'function') {
      window.screen.orientation.lock('landscape').catch(() => {
        // Orientation lock not supported or permission denied.
        // FullscreenSignaturePad will enforce landscape canvas proportions as a fallback.
      });
    }
    setIsFullscreen(true);
  };

  const closeFullscreen = (save = false) => {
    // Release orientation lock so the rest of the app behaves normally.
    if (window.screen.orientation && typeof window.screen.orientation.unlock === 'function') {
      try { window.screen.orientation.unlock(); } catch (e) { /* ignore */ }
    }

    if (save && fullscreenCanvasRef.current) {
      const fsCanvas = fullscreenCanvasRef.current;

      // ── Landscape normalisation ──────────────────────────────────────────
      // Always save the signature as a landscape image regardless of the
      // canvas orientation.  If the canvas ended up portrait (e.g. orientation
      // lock failed on an older browser), rotate the pixel buffer 90° clockwise
      // so the output is always wider than it is tall.
      let preview;
      if (fsCanvas.height > fsCanvas.width) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = fsCanvas.height; // swap dimensions
        tempCanvas.height = fsCanvas.width;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.save();
        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate(Math.PI / 2); // 90° clockwise → landscape
        tempCtx.drawImage(fsCanvas, -fsCanvas.width / 2, -fsCanvas.height / 2);
        tempCtx.restore();
        preview = tempCanvas.toDataURL('image/png');
      } else {
        preview = fsCanvas.toDataURL('image/png');
      }
      // ────────────────────────────────────────────────────────────────────

      setSignaturePreview(preview);

      // Also copy to main (small) canvas so it shows the preview thumbnail.
      if (canvasRef.current) {
        const mainCanvas = canvasRef.current;
        const ctx = mainCanvas.getContext('2d');
        ctx.clearRect(0, 0, mainCanvas.width, mainCanvas.height);
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, mainCanvas.width, mainCanvas.height);
        };
        img.src = preview;
      }
      setHasDrawn(true);
    }
    setIsFullscreen(false);
  };

  const saveSignature = async () => {
    try {
      setSaving(true);
      
      let signatureData;
      
      if (signatureType === 'draw') {
        // Use the preview if available, otherwise the main canvas
        if (signaturePreview) {
          signatureData = signaturePreview;
        } else {
          const canvas = canvasRef.current;
          signatureData = canvas.toDataURL('image/png');
        }
      } else if (signatureType === 'type') {
        if (!typedSignature.trim()) {
          throw new Error('Please enter your signature');
        }
        signatureData = typedSignature.trim();
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:3000/api/v1'}/signature-pad/session/${sessionToken}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data: signatureData,
          signature_type: signatureType === 'draw' ? 'drawn' : 'typed',
          device_info: navigator.userAgent || null,
          location: signerLocation || null
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save signature');
      }

      if (result.success) {
        setCompleted(true);
        setTimeout(() => { window.close(); }, 3000);
      } else {
        throw new Error(result.error || 'Failed to save signature');
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading signature pad...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => window.close()} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">Close</button>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Signature Saved!</h2>
          <p className="text-gray-600 mb-4">Your signature has been saved successfully. This window will close automatically.</p>
          <div className="text-sm text-gray-500">
            <p>Project: {sessionData?.client_project}</p>
            <p>Reference: {sessionData?.reference_id}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <PenTool className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Digital Signature</h1>
                <p className="text-sm text-gray-600">
                  {sessionData?.client_project} - {sessionData?.reference_id}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>
                Expires: {(() => { const d = new Date(sessionData?.expires_at); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Signature Type Selector */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Choose signature method:</h3>
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-md">
              <button
                onClick={() => setSignatureType('draw')}
                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  signatureType === 'draw' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <PenTool className="h-4 w-4 mr-2" />
                Draw
              </button>
              <button
                onClick={() => setSignatureType('type')}
                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  signatureType === 'type' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Type className="h-4 w-4 mr-2" />
                Type
              </button>
            </div>
          </div>

          {/* Signature Input Area */}
          <div className="p-6">
            {signatureType === 'draw' && (
              <div className="space-y-4">
                {/* Signature Preview (if drawn) */}
                {signaturePreview ? (
                  <div className="space-y-4">
                    <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Signature Preview</span>
                        </div>
                        <button
                          onClick={() => { setSignaturePreview(null); setHasDrawn(false); openFullscreen(); }}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                        >
                          <RotateCcw className="h-4 w-4" />
                          <span>Re-draw</span>
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border p-2">
                        <img
                          src={signaturePreview}
                          alt="Your signature"
                          className="w-full h-auto max-h-48 object-contain"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Draw Area - Tap to go fullscreen */}
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                      onClick={openFullscreen}
                    >
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={200}
                        className="w-full border rounded-lg cursor-pointer bg-white pointer-events-none"
                      />
                      <div className="flex items-center justify-center mt-3 text-gray-500">
                        <Maximize2 className="h-4 w-4 mr-2" />
                        <span className="text-sm">Tap to open full-screen drawing pad</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Open the drawing pad to sign</p>
                      <button onClick={clearCanvas} className="text-sm text-gray-600 hover:text-gray-800 underline">Clear</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {signatureType === 'type' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type your full name:</label>
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {typedSignature && (
                  <div className="border rounded-lg p-6 bg-gray-50">
                    <p className="text-3xl font-serif text-center text-gray-800">{typedSignature}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-t">
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => window.close()}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveSignature}
                disabled={saving || (signatureType === 'draw' && !hasDrawn && !signaturePreview) || (signatureType === 'type' && !typedSignature.trim())}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Signature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Instructions:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Choose your preferred signature method above</li>
            <li>• For drawing: Tap the canvas to open full-screen drawing mode</li>
            <li>• For typing: Enter your full name as it should appear</li>
            <li>• Click "Save Signature" when you're satisfied with your signature</li>
            <li>• This window will close automatically after saving</li>
          </ul>
        </div>
      </main>

      {/* Full-screen Signature Drawing Overlay */}
      {isFullscreen && (
        <FullscreenSignaturePad
          canvasRef={fullscreenCanvasRef}
          onStartDrawing={startDrawing}
          onDraw={draw}
          onStopDrawing={stopDrawing}
          onClear={clearCanvas}
          onSave={() => closeFullscreen(true)}
          onCancel={() => closeFullscreen(false)}
        />
      )}
    </div>
  );
};

// ─── Full-screen Signature Pad Overlay ──────────────
const FullscreenSignaturePad = ({ canvasRef, onStartDrawing, onDraw, onStopDrawing, onClear, onSave, onCancel }) => {
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const availableWidth  = rect.width  - 32; // 16px padding each side
        const availableHeight = rect.height - 16; // minimal top/bottom padding

        let w, h;
        if (availableHeight > availableWidth) {
          // ── Portrait viewport (orientation lock failed / not supported) ──
          // Force a landscape aspect ratio so the canvas is always wider than
          // it is tall.  A 2.5 : 1 ratio gives a comfortable signing strip
          // that fits within the portrait screen without scrolling.
          w = availableWidth;
          h = Math.floor(w / 2.5);
        } else {
          // ── Landscape viewport (orientation lock succeeded, or desktop) ──
          // Use the full available space.
          w = availableWidth;
          h = availableHeight;
        }

        setCanvasSize({ width: w, height: h });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);
    // Re-measure after a short delay to catch browser chrome reflow on mobile
    const timer = setTimeout(updateSize, 150);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
      clearTimeout(timer);
    };
  }, []);

  // Add non-passive touch listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const preventScroll = (e) => { e.preventDefault(); };
    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [canvasRef, canvasSize]);

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col" style={{ touchAction: 'none' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b shrink-0">
        <div className="flex items-center space-x-2">
          <PenTool className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-gray-900">Draw Your Signature</span>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Canvas Area */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center p-4 bg-gray-100 overflow-hidden">
        {canvasSize.width > 0 && (
          <canvas
            ref={canvasRef}
            width={canvasSize.width * 2}
            height={canvasSize.height * 2}
            className="bg-white rounded-xl shadow-lg cursor-crosshair touch-none"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              touchAction: 'none',
            }}
            onMouseDown={onStartDrawing}
            onMouseMove={onDraw}
            onMouseUp={onStopDrawing}
            onMouseLeave={onStopDrawing}
            onTouchStart={onStartDrawing}
            onTouchMove={onDraw}
            onTouchEnd={onStopDrawing}
          />
        )}
      </div>

      {/* Bottom Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t shrink-0 safe-area-bottom">
        <button
          onClick={onClear}
          className="flex items-center space-x-2 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          <span className="text-sm font-medium">Clear</span>
        </button>
        <div className="flex items-center space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="flex items-center space-x-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Done</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePadApp;
